<template>
  <div class="app-root h-screen bg-cockpit-layout-background text-slate-900 overflow-hidden">
    <div class="flex h-full w-full p-6">
      <SideNav :logo="logo">
        <template v-if="shouldDisplayProfile" #profile>
          <slot v-if="slots['side-nav-profile']" name="side-nav-profile" />
          <div v-else-if="isUserInfoLoading" class="flex animate-pulse items-center gap-2 rounded-sm bg-white">
            <div class="h-[52px] w-[52px] rounded-sm bg-slate-100" />
            <div class="flex min-w-0 flex-1 flex-col gap-2">
              <div class="h-4 w-3/4 rounded bg-slate-100" />
              <div class="h-3 w-1/2 rounded bg-slate-100" />
            </div>
            <div class="h-10 w-10 rounded-full bg-slate-100" />
          </div>
          <UserInfo
            v-else-if="userInfo"
            v-bind="displayedUserInfo"
            @logout="onUserInfoLogout"
            @update:select-value="onUserInfoSelectValueUpdate"
          >
            <template v-if="slots['user-info-actions']" #actions>
              <slot name="user-info-actions" />
            </template>
          </UserInfo>
        </template>

        <template v-if="shouldDisplaySections" #sections>
          <div v-if="areSideNavSectionsLoading" class="flex animate-pulse flex-col gap-5">
            <div class="flex flex-col gap-2">
              <div class="h-4 w-1/2 rounded bg-slate-100" />
              <div class="h-3 w-3/4 rounded bg-slate-100" />
            </div>
            <div class="flex flex-col gap-2">
              <div v-for="index in 3" :key="index" class="h-10 rounded bg-slate-100" />
            </div>
            <div class="h-px bg-slate-100" />
            <div class="flex flex-col gap-2">
              <div class="h-4 w-1/3 rounded bg-slate-100" />
              <div v-for="index in 2" :key="index" class="h-10 rounded bg-slate-100" />
            </div>
          </div>
          <slot v-else name="side-nav-sections" />
        </template>

        <template v-if="slots['side-nav-footer']" #footer>
          <slot name="side-nav-footer" />
        </template>
      </SideNav>

      <main class="flex-1 h-full overflow-y-auto rounded-2xl">
        <slot v-if="!isContentLoading" />
        <div v-else class="flex h-full w-full items-center justify-center">
          <Spinner />
        </div>
      </main>
    </div>

    <slot name="overlay" />
  </div>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue';
import { Spinner } from '@kiplin/web-common/design-system';
import SideNav from '@design-system/side-nav/application/component/SideNav/SideNav.vue';
import UserInfo from '@design-system/side-nav/application/component/UserInfo/UserInfo.vue';
import type { MainLayoutComponentProps, MainLayoutComponentUserInfo } from './MainLayoutComponent.types';

const props = withDefaults(defineProps<MainLayoutComponentProps>(), {
  userInfo: undefined,
  isUserInfoLoading: false,
  areSideNavSectionsLoading: false,
  isContentLoading: false,
  userInfoSelectLabel: undefined,
});

const emit = defineEmits<{
  logout: [];
  'update:userInfoSelectValue': [value: string];
}>();

const slots = useSlots();
const shouldDisplayProfile = computed(
  () => Boolean(slots['side-nav-profile']) || props.isUserInfoLoading || props.userInfo,
);
const shouldDisplaySections = computed(() => Boolean(slots['side-nav-sections']) || props.areSideNavSectionsLoading);
const displayedUserInfo = computed<MainLayoutComponentUserInfo>(() => {
  const userInfo = props.userInfo ?? {
    fullName: '',
    email: '',
    selectOptions: [],
  };

  return {
    ...userInfo,
    selectLabel: props.userInfoSelectLabel ?? userInfo.selectLabel,
  };
});

const onUserInfoSelectValueUpdate = (value: string) => {
  emit('update:userInfoSelectValue', value);
};

const onUserInfoLogout = () => {
  emit('logout');
};
</script>
